import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, HeartPulse, ClipboardList } from "lucide-react";
import { PageShell } from "../components/layout/PageShell";
import { PageHero } from "../components/layout/PageHero";
import { CtaBand } from "../components/layout/CtaBand";

const Section = ({ eyebrow, title, children, testId, className = "" }) => (
  <section className={`py-14 sm:py-20 ${className}`} data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h2 className="font-serif text-3xl sm:text-4xl font-medium text-[#292A26] tracking-tight mt-3 max-w-2xl">{title}</h2>
      <div className="mt-6">{children}</div>
    </div>
  </section>
);

export default function AboutPage() {
  return (
    <PageShell title="About">
      <PageHero
        testId="about-hero"
        eyebrow="About Mary Pawpins"
        title="Hello, I'm Carly."
        copy="Founder of Mary Pawpins Pet Care, based in Inverness and built around the kind of care I would want for my own animals."
        image="/images/marypawpins/carly-birch-log-autumn-wide.webp"
        alt="Carly sitting on a fallen birch with her cocker spaniel in autumn woodland"
        imgPosition="object-[50%_22%]"
      />

      <section className="py-14 sm:py-20" data-testid="about-intro">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg leading-relaxed text-[#575953]">
              I personally look after every pet on my books, whether that is a regular
              walk, a home visit or staying with them while their owners are away.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#575953]">
              What matters most to me is getting to know the animals properly. Their
              routines, personalities, the things they enjoy and the things they would
              rather avoid. It makes a difference, particularly when someone is trusting
              you with their pet and their home.
            </p>
            <div className="mt-8 rounded-3xl bg-[#E7E2D8]/60 border border-[#DDD7CB] p-7">
              <h3 className="font-serif text-xl font-medium text-[#292A26]">The name behind the business</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Mary Pawpins was inspired by a client who once said, "You're just
                like our Mary Poppins." They knew they could depend on me, that I
                genuinely cared, and that I brought a wee bit of magic to their
                pets' lives.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                The name stuck, and with a little twist, Mary Pawpins was born. 🤎
              </p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src="/images/marypawpins/carly-three-dogs-log-portrait.webp"
              alt="Carly with three dogs on a fallen birch in autumn woodland"
              className="rounded-3xl w-full aspect-[4/5] object-cover shadow-[0_20px_60px_rgba(41,42,38,0.12)]"
            />
          </div>
        </div>
      </section>

      <Section eyebrow="The home crowd" title="Mocha & Jingle." testId="about-pets" className="bg-[#EFECE3]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
          <div className="bg-white rounded-3xl border border-[#DDD7CB] overflow-hidden">
            <img src="/images/marypawpins/mocha-cockapoo-pink-coat.webp" alt="Mocha the cockapoo in her pink coat on a forest walk" className="w-full aspect-[4/3] object-cover" data-testid="mocha-photo" />
            <div className="p-7">
              <h3 className="font-serif text-xl font-medium text-[#292A26]">Mocha</h3>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#B9A58D] font-medium">The cockapoo</p>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Chief of quality control. If a walking route passes the Mocha test, it's a good one.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-[#DDD7CB] overflow-hidden">
            <img src="/images/marypawpins/jingle-black-cat.webp" alt="Jingle the black cat among the garden flowers" className="w-full aspect-[4/3] object-cover" data-testid="jingle-photo" />
            <div className="p-7">
              <h3 className="font-serif text-xl font-medium text-[#292A26]">Jingle</h3>
              <p className="mt-1 text-xs uppercase tracking-[0.16em] text-[#B9A58D] font-medium">The black cat</p>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Runs the household, supervises feeding time, and tolerates the rest of us.
              </p>
            </div>
          </div>
        </div>
        <p className="mt-6 text-sm leading-relaxed text-[#575953] max-w-2xl">
          They're the reason I understand exactly what it means to trust someone else
          with your animals, and why Mary Pawpins cares for cats as well as dogs.
        </p>
      </Section>

      <Section eyebrow="Beyond Mary Pawpins" title="A busy, balanced week." testId="about-studies">
        <p className="text-base sm:text-lg leading-relaxed text-[#575953] max-w-2xl">
          Alongside Mary Pawpins, I'm studying a master's degree in psychotherapy and
          counselling, having completed my psychology degree and counselling skills
          training. It's a busy combination, but one I genuinely enjoy, and it means
          my working week is focused around regular walks on Mondays, Tuesdays and Fridays.
        </p>
      </Section>

      <Section eyebrow="The approach" title="Walks, but considered." testId="about-walks" className="bg-[#EFECE3]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-base sm:text-lg leading-relaxed text-[#575953]">
              Group walks are chosen around the dogs involved, with attention to their
              temperament, energy and how they get along with others. Mary's Meadow
              provides a brilliant space for suitable dogs to enjoy, while other fields
              and selected forest routes offer variety when appropriate.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#575953]">
              The aim is not to put every dog into the same group, but to find an
              arrangement that suits them.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img
              src="/images/marypawpins/carly-walking-pack-forest-path.webp"
              alt="Carly walking with a group of dogs on a forest path"
              className="rounded-3xl w-full aspect-[4/5] object-cover"
            />
          </div>
        </div>
      </Section>

      <Section eyebrow="The sensible bit" title="Trained, insured, prepared." testId="about-credentials">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <img src="/images/marypawpins/carly-first-aid-cpr.jpg" alt="Carly practising canine CPR during first-aid training" className="rounded-2xl w-full aspect-square object-cover" />
              <img src="/images/marypawpins/carly-first-aid-bandaging.webp" alt="Carly practising bandaging during canine first-aid training" className="rounded-2xl w-full aspect-square object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="text-base sm:text-lg leading-relaxed text-[#575953]">
              Fully insured, canine first-aid trained, and committed to making decisions
              based on the individual animal's needs and safety.
            </p>
            <ul className="mt-6 space-y-4">
              {[
                { icon: ShieldCheck, text: "Commercial pet-business insurance, including public liability and animal injury cover" },
                { icon: HeartPulse, text: "Canine first-aid trained, with hands-on practical assessment" },
                { icon: ClipboardList, text: "Careful introductions, group suitability checks, emergency contacts and clear communication" },
              ].map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-[#F4ECE9] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#3F4A38]" />
                  </span>
                  <span className="text-sm leading-relaxed text-[#575953] pt-2">{text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section eyebrow="Why it matters" title="One person, on purpose." testId="about-one-person" className="bg-[#EFECE3]">
        <p className="text-base sm:text-lg leading-relaxed text-[#575953] max-w-2xl">
          Mary Pawpins is run solely by me, and that's a strength, not a limitation.
          You know who is collecting your dog, who is staying in your home and who is
          responsible for the care. That continuity lets me build familiar relationships
          and notice the little things that matter to individual animals.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[#575953] max-w-2xl">
          It also means clear availability: regular walking on Mondays, Tuesdays and
          Fridays, with sitting and visits arranged individually. No team, no rotating
          faces. Just me.
        </p>
      </Section>

      <CtaBand
        testId="about-cta"
        heading="Come and say hello."
        copy="If you are looking for regular walks or someone to care for your pets while you are away, I'd be happy to hear a little about them and what you need."
      />
    </PageShell>
  );
}
